import { useState } from 'react';
import type {
  CandidateRecheckJobV1,
  CandidateRecheckStoredResultV1,
  PreviewEligibilityV1,
  StoredFailedCandidateV1,
} from '@djgg/creations';
import { useServices } from '../services/context.js';

const CHECKS = [
  ['artifact-integrity', 'Artifact integrity'],
  ['deterministic-fixtures', 'Deterministic fixtures'],
  ['browser-smoke', 'Browser smoke'],
  ['input-replay', 'Exact input replay'],
  ['media-inventory', 'Media inventory'],
] as const;

type Inspection = {
  candidate: StoredFailedCandidateV1;
  preview: PreviewEligibilityV1;
  recheck: CandidateRecheckJobV1 | null;
  result: CandidateRecheckStoredResultV1 | null;
};

const resultLabel = (result: CandidateRecheckStoredResultV1 | null): string => {
  if (!result) return 'No result recorded';
  const overall = result.result.overall;
  return typeof overall === 'string' ? overall : 'Result retained';
};

/** Operator diagnostics only. Candidate bytes are never fetched by this panel. */
export function FailedCandidateInspectionPanel() {
  const services = useServices();
  const [candidateId, setCandidateId] = useState('');
  const [recheckJobId, setRecheckJobId] = useState('');
  const [mode, setMode] = useState<'historical-exact' | 'current-harness'>('historical-exact');
  const [checks, setChecks] = useState<string[]>(['artifact-integrity']);
  const [inspection, setInspection] = useState<Inspection | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = async (jobId = recheckJobId) => {
    const id = candidateId.trim();
    if (!id) return;
    setBusy(true);
    setError('');
    try {
      setInspection(await services.callable.getFailedCandidateInspection({
        candidateId: id,
        ...(jobId.trim() ? { recheckJobId: jobId.trim() } : {}),
      }));
    } catch {
      setInspection(null);
      setError('Candidate diagnostics are unavailable or this identifier was not found.');
    } finally {
      setBusy(false);
    }
  };

  const requestRecheck = async () => {
    if (checks.length === 0) {
      setError('Choose at least one diagnostic check.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const { job } = await services.callable.requestCandidateRecheck({ candidateId: candidateId.trim(), mode, checks });
      setRecheckJobId(job.jobId);
      await load(job.jobId);
    } catch {
      setError('The bounded recheck could not be queued. No diagnostic result was invented.');
      setBusy(false);
    }
  };

  const cancelRecheck = async () => {
    const jobId = inspection?.recheck?.jobId;
    if (!jobId) return;
    setBusy(true);
    setError('');
    try {
      await services.callable.cancelCandidateRecheck({ candidateId: candidateId.trim(), recheckJobId: jobId });
      await load(jobId);
    } catch {
      setError('The recheck cancellation could not be recorded. Reload its current state before retrying.');
      setBusy(false);
    }
  };

  return (
    <section className="panel" data-testid="failed-candidate-inspection" style={{ marginTop: 16 }}>
      <h2 style={{ fontSize: 18 }}>Failed candidate inspection</h2>
      <p className="muted">Private metadata and provider-free diagnostics. This screen never loads retained candidate bytes.</p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input aria-label="Candidate ID" value={candidateId} onChange={(event) => setCandidateId(event.target.value)} placeholder="Candidate ID" />
        <input aria-label="Recheck job ID" value={recheckJobId} onChange={(event) => setRecheckJobId(event.target.value)} placeholder="Recheck job ID (optional)" />
        <button className="btn btn--ghost btn--sm" type="button" disabled={busy || !candidateId.trim()} onClick={() => void load()}>
          {busy ? 'Loading…' : 'Inspect'}
        </button>
      </div>
      {error && <p className="error-text" role="alert">{error}</p>}
      {inspection && (
        <div style={{ marginTop: 12 }}>
          <p><strong>{inspection.candidate.candidate.candidateId}</strong> · v{inspection.candidate.candidate.version} · {inspection.candidate.candidate.failure.stage}</p>
          <ul>
            <li>Source archive: {inspection.candidate.candidate.source ? 'retained privately' : 'unavailable'}</li>
            <li>Runnable output: {inspection.candidate.candidate.dist?.inventory.length ? `${inspection.candidate.candidate.dist.inventory.length} immutable files` : 'unavailable'}</li>
            <li>Preview: {inspection.preview.status}{inspection.preview.reasonCodes.length ? ` — ${inspection.preview.reasonCodes.join(', ')}` : ''}</li>
            <li>Recheck: {inspection.recheck?.status ?? 'not requested'} · {resultLabel(inspection.result)}</li>
          </ul>
          <p className="muted">
            {inspection.preview.status === 'no-runnable-output'
              ? 'Play is unavailable because this candidate has no retained dist.'
              : inspection.preview.status === 'runtime-unsupported'
                ? 'Play is unavailable because a safe local-only runtime is not proven.'
                : 'Play remains unavailable until the isolated preview gateway is released and verified.'}
          </p>
          <fieldset disabled={busy} style={{ border: 0, padding: 0 }}>
            <legend><strong>Provider-free recheck</strong></legend>
            <select aria-label="Recheck mode" value={mode} onChange={(event) => setMode(event.target.value as typeof mode)}>
              <option value="historical-exact">Historical exact runtime</option>
              <option value="current-harness">Current harness (separate result)</option>
            </select>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '8px 0' }}>
              {CHECKS.map(([value, label]) => (
                <label key={value}>
                  <input
                    type="checkbox"
                    checked={checks.includes(value)}
                    onChange={(event) => setChecks(event.target.checked ? [...checks, value] : checks.filter((check) => check !== value))}
                  />{' '}{label}
                </label>
              ))}
            </div>
            <button className="btn btn--primary btn--sm" type="button" disabled={!candidateId.trim() || checks.length === 0} onClick={() => void requestRecheck()}>
              Queue bounded recheck
            </button>
            {inspection.recheck?.cancellationRequested && <span className="muted" style={{ marginLeft: 8 }}>Cancellation requested</span>}
            {inspection.recheck && !inspection.recheck.cancellationRequested && ['pending', 'claimed'].includes(inspection.recheck.status) && (
              <button className="btn btn--ghost btn--sm" type="button" onClick={() => void cancelRecheck()} style={{ marginLeft: 8 }}>
                Cancel recheck
              </button>
            )}
          </fieldset>
        </div>
      )}
    </section>
  );
}
